/**
 * CDP tracker výlohy – plain modulový singleton, ne hook.
 *
 * Musí být volatelný z cart.tsx callbacků i mimo React, proto žádný kontext.
 * Eventy se dávkují (timer 5 s ∨ fronta ≥ 10 ∨ odchod ze stránky) a posílají
 * keepalive fetchem na POST /api/events. Vlastní fetch, ne `request<T>`
 * z api.ts – ten není exportovaný a má jiné sémantiky (vyhazuje ShopError);
 * tracking je fire-and-forget a NIKDY nesmí shodit UI.
 *
 * Consent gate: bez analytics=true se event vůbec nezařadí (žádná fronta,
 * žádný request). Jediná výjimka je consent_changed – účtenka souhlasu,
 * odesílá se i při odmítnutí. Server gate drží totéž pravidlo nezávisle.
 */
import { CONSENT_VERSION, readConsent, type ConsentState } from './consent';
import { captureSessionContext, getSessionContext, getSessionId, getVisitorId, randomId } from './ids';

const EVENTS_URL = '/api/events';
const IDENTIFY_URL = '/api/events/identify';
const CSRF_HEADER = 'x-requested-with';
const CSRF_VALUE = 'zooshopik-admin';

/** Kontrakt ingest API: max 20 eventů na dávku. */
const BATCH_LIMIT = 20;
/** Strop fronty – při přetečení padají nejstarší eventy (fail-open). */
const QUEUE_CAP = 100;
/** Flush nejpozději za 5 s od prvního eventu ve frontě. */
const FLUSH_DELAY_MS = 5000;
/** Flush hned, jakmile se nasbírá aspoň tolik eventů. */
const FLUSH_THRESHOLD = 10;
/** Neúspěšná dávka se vrátí do fronty nejvýš jednou. */
const MAX_ATTEMPTS = 1;

interface QueuedEvent {
  readonly event_id: string;
  readonly event_name: string;
  readonly occurred_at: string;
  readonly properties: Record<string, unknown>;
  /** Kolikrát už dávka s tímhle eventem selhala. */
  readonly attempts: number;
}

let queue: QueuedEvent[] = [];
let flushTimer: number | undefined;
let listenersRegistered = false;

/** Jednorázová registrace flush listenerů (volá TrackingProvider při mountu). */
export function initTracker(): void {
  if (listenersRegistered) return;
  listenersRegistered = true;
  // Zachycení vstupního kontextu (UTM/referrer/landing) hned při startu –
  // první page_viewed už ho ponese.
  captureSessionContext();
  // `pagehide` + `visibilitychange` pokrývají zavření záložky, navigaci
  // jinam i odchod na platební bránu Comgate. Keepalive fetch přežije unload.
  window.addEventListener('pagehide', () => flush({ keepalive: true }));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush({ keepalive: true });
  });
}

/**
 * Zařadí event do fronty. Bez souhlasu s analytikou se tiše zahodí ještě
 * před frontou – nerozhodnutý stav se chová jako odmítnutí.
 */
export function track(eventName: string, properties: Record<string, unknown> = {}): void {
  if (readConsent().analytics !== true) return;
  enqueue(eventName, properties);
}

/**
 * Jediný event, který obchází consent gate: consent_changed je účtenka
 * souhlasu a odesílá se při každé změně volby, i té odmítavé. Serverová
 * strana ho pouští jako ESSENTIAL. Flush jde hned – při odmítnutí by se
 * jiná příležitost k odeslání už nenaskytla.
 */
export function trackConsentChanged(next: ConsentState): void {
  enqueue('consent_changed', { analytics: next.analytics, marketing: next.marketing });
  flush();
}

/**
 * Visitor/session klíče pro checkout body. Bez analytics souhlasu vrací
 * undefined – objednávka pak odejde bez behaviorální vazby (revenue pravdu
 * drží server nezávisle).
 */
export function getTrackingContext(): { visitorId: string; sessionId: string } | undefined {
  if (readConsent().analytics !== true) return undefined;
  return { visitorId: getVisitorId(), sessionId: getSessionId() };
}

/**
 * Ohlásí serveru „tohle zařízení patří přihlášenému zákazníkovi".
 * Identita se bere VÝHRADNĚ ze session cookie (proto credentials:'include');
 * tělo nese jen klíče zařízení. Volat jen se souhlasem s analytikou.
 */
export function identifyLoggedIn(): void {
  if (readConsent().analytics !== true) return;
  try {
    void fetch(IDENTIFY_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json', [CSRF_HEADER]: CSRF_VALUE },
      credentials: 'include',
      body: JSON.stringify({ visitor_id: getVisitorId(), session_id: getSessionId() }),
    }).catch((error: unknown) => {
      console.debug('cdp identify failed', error);
    });
  } catch (error) {
    console.debug('cdp identify failed', error);
  }
}

/**
 * Odešle frontu na server. `keepalive` použij při odchodu ze stránky –
 * request přežije unload (na rozdíl od obyčejného fetche) a hlavičky umí
 * (na rozdíl od sendBeacon), takže CSRF ochrana serveru zůstává v platnosti.
 * Fire-and-forget: chyba = tichý console.debug + max 1 návrat do fronty.
 */
export function flush(options: { keepalive?: boolean } = {}): void {
  if (flushTimer !== undefined) {
    window.clearTimeout(flushTimer);
    flushTimer = undefined;
  }
  if (queue.length === 0) return;

  // Gate i tady: souhlas mohl být mezitím odvolán. Bez analytics projde
  // jen consent_changed, zbytek fronty se zahodí.
  const analytics = readConsent().analytics === true;
  const sendable = analytics ? queue : queue.filter((event) => event.event_name === 'consent_changed');
  queue = [];
  if (sendable.length === 0) return;

  // Kontrakt povoluje max 20 eventů na request – větší fronta jde po dávkách.
  for (let start = 0; start < sendable.length; start += BATCH_LIMIT) {
    sendBatch(sendable.slice(start, start + BATCH_LIMIT), options.keepalive === true);
  }
}

/* ------------------------------------------------------------------ intern */

function enqueue(eventName: string, properties: Record<string, unknown>): void {
  pushToQueue({
    event_id: randomId('evt_'),
    event_name: eventName,
    occurred_at: new Date().toISOString(),
    properties,
    attempts: 0,
  });
  scheduleFlush();
}

function pushToQueue(event: QueuedEvent): void {
  // Přetečení: zahazujeme nejstarší – čerstvé chování je cennější a server
  // stejně deduplikuje podle event_id, takže ztráta je jen datová, ne chybová.
  while (queue.length >= QUEUE_CAP) queue.shift();
  queue.push(event);
}

function scheduleFlush(): void {
  if (queue.length >= FLUSH_THRESHOLD) {
    flush();
    return;
  }
  if (flushTimer !== undefined) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = undefined;
    flush();
  }, FLUSH_DELAY_MS);
}

/** Obálka dávky přesně dle ingest kontraktu POST /api/events. */
function buildEnvelope(events: readonly QueuedEvent[]): Record<string, unknown> {
  const consent = readConsent();
  const sessionCtx = getSessionContext();
  return {
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    sent_at: new Date().toISOString(),
    consent: {
      analytics: consent.analytics,
      marketing: consent.marketing,
      version: CONSENT_VERSION,
    },
    context: {
      url: window.location.href,
      path: window.location.pathname,
      referrer: sessionCtx.referrer,
      utm: sessionCtx.utm,
      language: document.documentElement.lang || 'cs',
      viewport: { w: window.innerWidth, h: window.innerHeight },
    },
    events: events.map(({ attempts: _attempts, ...event }) => event),
  };
}

function sendBatch(events: readonly QueuedEvent[], keepalive: boolean): void {
  let body: string;
  try {
    body = JSON.stringify(buildEnvelope(events));
  } catch (error) {
    console.debug('cdp flush failed', error);
    return;
  }

  void fetch(EVENTS_URL, {
    method: 'POST',
    keepalive: true,
    // Ingest je anonymní – žádné cookies, identita jde jen přes /identify.
    credentials: 'omit',
    headers: { 'content-type': 'application/json', [CSRF_HEADER]: CSRF_VALUE },
    body,
  })
    .then((response) => {
      if (!response.ok) retryBatch(events, keepalive, `http ${response.status}`);
    })
    .catch((error: unknown) => {
      retryBatch(events, keepalive, error);
    });
}

/** Neúspěšná dávka se vrátí do fronty – ale jen jednou, pak se zahodí. */
function retryBatch(events: readonly QueuedEvent[], keepalive: boolean, reason: unknown): void {
  console.debug('cdp flush failed', reason);
  // Při odchodu ze stránky už nemá smysl frontovat – stránka končí.
  if (keepalive) return;
  for (const event of events) {
    if (event.attempts >= MAX_ATTEMPTS) continue;
    pushToQueue({ ...event, attempts: event.attempts + 1 });
  }
  if (queue.length > 0) scheduleFlush();
}
